import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import axios from 'axios';
import CommunityStats from '../components/CommunityStats';
import ProfileCompletionBanner from '../components/ProfileCompletionBanner';
import TrustBadge from '../components/TrustBadge';
import Navbar from '../components/Navbar';
import ChannelCard from '../components/ChannelCard';

function AiContentEngineCard() {
  const [status, setStatus] = React.useState({ running: false, lastRun: null });
  const [triggering, setTriggering] = React.useState(false);

  React.useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/admin/ai-content/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (e) { /* ignore */ }
  };

  const handleTrigger = async () => {
    if (!window.confirm('Are you sure you want to trigger AI content generation? This will make API calls to OpenAI and generate posts across all channels.')) {
      return;
    }

    setTriggering(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/admin/ai-content/trigger`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok) {
        alert(`✅ AI Content Cycle Complete!\n\nPosts generated: ${data.postsGenerated}\nPosts discarded: ${data.postsDiscarded}\nAPI calls: ${data.apiCallsMade}\nCost: ~$${data.estimatedCostUsd?.toFixed(4) || '0'}\nErrors: ${data.errors?.length || 0}`);
        fetchStatus();
      } else {
        alert(`❌ Failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`❌ Error: ${err.message}`);
    } finally {
      setTriggering(false);
    }
  };

  const formatLastRun = () => {
    if (!status.lastRun) return 'Never run';
    const date = new Date(status.lastRun.completed_at);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor(diffMs / (1000 * 60));

    let timeAgo;
    if (diffMins < 60) timeAgo = `${diffMins}m ago`;
    else if (diffHours < 24) timeAgo = `${diffHours}h ago`;
    else timeAgo = `${Math.floor(diffHours / 24)}d ago`;

    return `Last run: ${timeAgo} (${status.lastRun.posts_generated} posts, triggered by ${status.lastRun.triggered_by})`;
  };

  const isDisabled = status.running || triggering;

  // Batch generation state
  const [showBatch, setShowBatch] = React.useState(false);
  const [batchChannels, setBatchChannels] = React.useState([]);
  const [batchCategory, setBatchCategory] = React.useState('');
  const [batchChannel, setBatchChannel] = React.useState('');
  const [batchCount, setBatchCount] = React.useState(10);
  const [batchRunning, setBatchRunning] = React.useState(false);

  const fetchBatchChannels = async () => {
    if (batchChannels.length > 0) return;
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/admin/moderators/channels', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBatchChannels(data.channels || []);
    } catch (e) {}
  };

  const batchCategories = [...new Map(batchChannels.map(ch => [ch.category_id, { category_id: ch.category_id, category_name: ch.category_name }])).values()];
  const batchFilteredChannels = batchCategory ? batchChannels.filter(ch => ch.category_id === parseInt(batchCategory)) : [];

  const handleBatchGenerate = async () => {
    if (!batchChannel || batchCount < 1) return;
    if (!window.confirm(`Generate ${batchCount} AI posts for this channel? They'll go to the review queue.`)) return;
    setBatchRunning(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/admin/ai-content/batch', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ channelId: parseInt(batchChannel), count: batchCount })
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.message);
        setShowBatch(false);
      } else {
        alert(data.error || 'Failed');
      }
    } catch (e) { alert('Error: ' + e.message); }
    setBatchRunning(false);
  };

  return (
    <div className="card" style={{ borderLeft: '4px solid #8b5cf6' }}>
      <h3>🤖 AI Content Engine</h3>
      <p>Generate community posts for channels using AI. Runs automatically every 24h.</p>
      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
        {status.running ? '⏳ Cycle currently running...' : formatLastRun()}
      </p>
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary"
          style={{ background: isDisabled ? '#9ca3af' : '#8b5cf6', cursor: isDisabled ? 'not-allowed' : 'pointer' }}
          disabled={isDisabled}
          onClick={handleTrigger}
        >
          {triggering ? 'Generating...' : status.running ? 'Cycle Running...' : 'Run Full Cycle'}
        </button>
        <button
          className="btn btn-primary"
          style={{ background: '#6366f1' }}
          onClick={() => { setShowBatch(!showBatch); fetchBatchChannels(); }}
        >
          {showBatch ? '✕ Close' : '📦 Generate Batch'}
        </button>
      </div>

      {showBatch && (
        <div style={{ marginTop: '16px', padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <p style={{ fontSize: '13px', color: '#374151', marginBottom: '10px', fontWeight: 500 }}>Generate multiple posts for a specific channel:</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
            <select value={batchCategory} onChange={(e) => { setBatchCategory(e.target.value); setBatchChannel(''); }} style={{ flex: 1, minWidth: '140px', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}>
              <option value="">Category...</option>
              {batchCategories.map(c => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
            </select>
            <select value={batchChannel} onChange={(e) => setBatchChannel(e.target.value)} disabled={!batchCategory} style={{ flex: 1, minWidth: '140px', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', opacity: batchCategory ? 1 : 0.5 }}>
              <option value="">Channel...</option>
              {batchFilteredChannels.map(ch => <option key={ch.channel_id} value={ch.channel_id}>{ch.name}</option>)}
            </select>
            <input type="number" value={batchCount} onChange={(e) => setBatchCount(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))} min="1" max="50" style={{ width: '60px', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }} />
          </div>
          <button onClick={handleBatchGenerate} disabled={!batchChannel || batchRunning} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: (!batchChannel || batchRunning) ? '#9ca3af' : '#6366f1', color: '#fff', cursor: (!batchChannel || batchRunning) ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
            {batchRunning ? 'Starting...' : `Generate ${batchCount} Posts →`}
          </button>
          <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>Posts go to the review queue. Takes ~3s per post.</p>
        </div>
      )}
    </div>
  );
}

function ContentStudioCard() {
  const [isModerator, setIsModerator] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    fetch('/api/content-studio/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setIsModerator(data.isModerator))
      .catch(() => {});
  }, []);

  if (!isModerator) return null;

  return (
    <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #10b981' }} onClick={() => navigate('/content-studio')}>
      <h3>🌱 Content Studio</h3>
      <p>Review, edit, and create community posts for your assigned categories</p>
      <button className="btn btn-primary" style={{ marginTop: '12px', background: '#10b981' }}>
        Open Content Studio →
      </button>
    </div>
  );
}

function BecomeContributorCard() {
  const [isModerator, setIsModerator] = React.useState(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    fetch('/api/content-studio/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setIsModerator(data.isModerator))
      .catch(() => setIsModerator(false));
  }, []);

  // Don't show if already a moderator or still loading
  if (isModerator === null || isModerator) return null;

  return (
    <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #10b981' }} onClick={() => navigate('/contribute')}>
      <h3>🌱 Become a Community Contributor</h3>
      <p>Help others by sharing your experience. Review and create content for communities you know well.</p>
      <button className="btn btn-primary" style={{ marginTop: '12px', background: '#10b981' }}>
        Apply to Contribute →
      </button>
    </div>
  );
}

function Dashboard() {
  const { user } = useAuth();
  const { isOrgUser, orgSlug, orgLoading } = useOrg();
  const params = useParams();
  const slug = params.slug || orgSlug;
  const p = (path) => (isOrgUser && slug) ? `/org/${slug}${path}` : path;
  const navigate = useNavigate();
  const [inviteCount, setInviteCount] = React.useState(0);
  const [notificationCount, setNotificationCount] = React.useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = React.useState(0);
  const [pendingRequestCount, setPendingRequestCount] = React.useState(0);
  const [channels, setChannels] = React.useState([]);

  React.useEffect(() => {
    fetchInviteCount();
    fetchNotificationCount();
    fetchUnreadMessageCount();
    fetchPendingRequestCount();

    // Poll for new messages, notifications, and invites every 20 seconds
    const interval = setInterval(() => {
      fetchUnreadMessageCount();
      fetchNotificationCount();
      fetchInviteCount();
      fetchPendingRequestCount();
    }, 20000);

    return () => clearInterval(interval);
  }, []);

  // Fetch channels after org context is loaded so orgScope param is correct
  React.useEffect(() => {
    if (!orgLoading) {
      fetchChannels();
    }
  }, [orgLoading, isOrgUser]);

  const fetchInviteCount = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/groups/invites/received', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInviteCount(response.data.invites.length);
    } catch (error) {
      console.error('Error fetching invites:', error);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/notifications/unread-count', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationCount(response.data.unreadCount);
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  const fetchUnreadMessageCount = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/messages/unread/count', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadMessageCount(response.data.unreadCount);
    } catch (error) {
      console.error('Error fetching unread message count:', error);
    }
  };

  const fetchPendingRequestCount = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/connections/requests/received', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingRequestCount(response.data.requests.length);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  };

  const fetchChannels = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const params = (isOrgUser && slug) ? '?orgScope=true' : '';
      const response = await axios.get(`/api/channels${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChannels(Array.isArray(response.data) ? response.data : response.data.channels || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  return (
    <div>
      <Navbar />

      <div className="container">
        <ProfileCompletionBanner />
        
        <div className="card">
          <h2>Welcome, {user?.first_name || 'User'}!</h2>
          <p>Your dashboard is ready. Start connecting with your community.</p>
        </div>

        <CommunityStats variant="card" />

        {/* My Spaces Section */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>My Spaces</h3>
            <span
              style={{ fontSize: '13px', color: '#7C3AED', cursor: 'pointer', fontWeight: '600' }}
              onClick={() => navigate((isOrgUser && slug) ? p('/communities') : '/b')}
            >
              Browse all →
            </span>
          </div>
          {channels.length > 0 ? (
            <div style={{
              display: 'flex',
              gap: '12px',
              overflowX: 'auto',
              paddingBottom: '8px',
              marginTop: '12px',
              WebkitOverflowScrolling: 'touch'
            }}>
              {channels.map(channel => (
                <ChannelCard key={channel.channel_id} channel={channel} />
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
              Join spaces by selecting identities in your profile.{' '}
              <span style={{ color: '#7C3AED', cursor: 'pointer', fontWeight: '500' }} onClick={() => navigate((isOrgUser && slug) ? p('/communities') : '/b')}>
                Or browse all spaces →
              </span>
            </p>
          )}
        </div>

        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(p('/discovery'))}>
          <h3>Discover People</h3>
          <p>Find people who share your identities in your area</p>
          <button className="btn btn-primary">Start Discovering →</button>
        </div>

        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(p('/connections'))}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div style={{ flex: 1 }}>
              <h3>Your Connections</h3>
              <p>View and manage your connections and requests</p>
            </div>
            {pendingRequestCount > 0 && (
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#dc3545',
                color: 'white',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {pendingRequestCount} new
              </span>
            )}
          </div>
          <button className="btn btn-primary" style={{ marginTop: '12px' }}>View Connections →</button>
        </div>

        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(p('/messages'))}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div style={{ flex: 1 }}>
              <h3>Messages</h3>
              <p>Chat with your connections</p>
            </div>
            {unreadMessageCount > 0 && (
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#dc3545',
                color: 'white',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {unreadMessageCount} new
              </span>
            )}
          </div>
          <button className="btn btn-primary" style={{ marginTop: '12px' }}>
            View Messages →
          </button>
        </div>

        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(p('/notifications'))}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div style={{ flex: 1 }}>
              <h3>Notifications</h3>
              <p>View warnings, alerts, and important messages</p>
            </div>
            {notificationCount > 0 && (
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#dc3545',
                color: 'white',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {notificationCount} new
              </span>
            )}
          </div>
          <button className="btn btn-primary" style={{ marginTop: '12px' }}>
            View Notifications →
          </button>
        </div>

        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(p('/groups'))}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div style={{ flex: 1 }}>
              <h3>Groups</h3>
              <p>Join groups and connect with people who share your interests</p>
            </div>
            {inviteCount > 0 && (
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#dc3545',
                color: 'white',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {inviteCount} invite{inviteCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <button className="btn btn-primary" style={{ marginTop: '12px' }}>
            Browse Groups →
          </button>
        </div>

        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(p('/events'))}>
          <h3>Events</h3>
          <p>Discover and attend community events and gatherings</p>
          <button className="btn btn-primary" style={{ marginTop: '12px' }}>
            Browse Events →
          </button>
        </div>

        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(p('/heroes'))}>
          <h3>Community Heroes</h3>
          <p>Celebrate notable figures and achievements from our community</p>
          <button className="btn btn-primary" style={{ marginTop: '12px' }}>
            View Heroes →
          </button>
        </div>

        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(p('/category-requests'))}>
          <h3>My Category Requests</h3>
          <p>Request new identity categories or view your submitted requests</p>
          <button className="btn btn-primary" style={{ marginTop: '12px' }}>
            View My Requests →
          </button>
        </div>

        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(p('/contact-us'))}>
          <h3>Contact Us</h3>
          <p>Send questions, feedback, or feature requests to our team</p>
          <button className="btn btn-primary" style={{ marginTop: '12px' }}>
            Send Feedback →
          </button>
        </div>

        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(p('/my-feedback'))}>
          <h3>My Feedback</h3>
          <p>View your submitted feedback and admin responses</p>
          <button className="btn btn-primary" style={{ marginTop: '12px' }}>
            View My Feedback →
          </button>
        </div>

        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate('/ambassador')}>
          <h3>🚀 Ambassador Program</h3>
          <p>Earn money by inviting people to Belong. Get your referral link and track your progress.</p>
          <button className="btn btn-primary" style={{ marginTop: '12px' }}>
            Earn with Referrals →
          </button>
        </div>

        <ContentStudioCard />

        <BecomeContributorCard />

        {user?.is_admin && (
          <>
            <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #9b59b6' }} onClick={() => navigate('/admin/category-requests')}>
              <h3>Review Category Requests</h3>
              <p>Review and approve/reject identity category requests from users</p>
              <button className="btn btn-primary" style={{ marginTop: '12px', background: '#9b59b6' }}>
                Admin Panel →
              </button>
            </div>

            <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #e74c3c' }} onClick={() => navigate('/admin/moderation')}>
              <h3>Admin Moderation</h3>
              <p>Review user reports and take moderation actions</p>
              <button className="btn btn-primary" style={{ marginTop: '12px', background: '#e74c3c' }}>
                Open Moderation Panel →
              </button>
            </div>

            <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #3498db' }} onClick={() => navigate('/admin/feedback')}>
              <h3>Feedback Management</h3>
              <p>Review and respond to user feedback and requests</p>
              <button className="btn btn-primary" style={{ marginTop: '12px', background: '#3498db' }}>
                Manage Feedback →
              </button>
            </div>

            <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #f59e0b' }} onClick={() => navigate('/admin/org-requests')}>
              <h3>🏢 Organization Requests</h3>
              <p>Review and approve organization signup requests</p>
              <button className="btn btn-primary" style={{ marginTop: '12px', background: '#f59e0b' }}>
                Review Requests →
              </button>
            </div>

            <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #10b981' }} onClick={() => navigate('/admin/referrals')}>
              <h3>📊 Referral Attribution</h3>
              <p>Track intern performance — signups, activation, and retention</p>
              <button className="btn btn-primary" style={{ marginTop: '12px', background: '#10b981' }}>
                View Performance →
              </button>
            </div>

            <AiContentEngineCard />

            <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #f59e0b' }} onClick={() => navigate('/admin/content-review')}>
              <h3>📝 Content Review Queue</h3>
              <p>Review, edit, and approve AI-generated posts before they go live</p>
              <button className="btn btn-primary" style={{ marginTop: '12px', background: '#f59e0b' }}>
                Review Content →
              </button>
            </div>

            <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #06b6d4' }} onClick={() => navigate('/admin/moderators')}>
              <h3>👥 Content Moderators</h3>
              <p>Assign users as content moderators for specific identity categories</p>
              <button className="btn btn-primary" style={{ marginTop: '12px', background: '#06b6d4' }}>
                Manage Moderators →
              </button>
            </div>

            <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #ec4899' }} onClick={() => navigate('/admin/contributor-applications')}>
              <h3>📋 Contributor Applications</h3>
              <p>Review and approve applications from users who want to contribute content</p>
              <button className="btn btn-primary" style={{ marginTop: '12px', background: '#ec4899' }}>
                Review Applications →
              </button>
            </div>
          </>
        )}

        <TrustBadge variant="banner" />
      </div>
    </div>
  );
}

export default Dashboard;

